/**
 * Writes entity collections into the query cache, fanning each one across its list query and every
 * entity's detail query.
 *
 * Both writers exist to keep the cache useful without a network — a farmer opening the app in the field
 * should find recent, relevant data already there. Neither is serving an online reader, so neither makes any
 * claim about the data's freshness or validity: a fetch that was already due still happens, and a mutation's
 * invalidation still stands. `../README.md` sets out what each kind of write is allowed to claim.
 *
 * Callers pick a policy by which function they call rather than by passing flags: a seed yields to anything
 * already cached, while a patch merges over cached values but never settles the query.
 *
 * Ordering against in-flight fetches is deliberately not arbitrated here: a refetch that started before a
 * patch can resolve after it and win. The next poll corrects it, which is cheaper than cancelling fetches
 * a screen is waiting on.
 */
import type { QueryKey } from "@tanstack/react-query";
import { queryClient } from "@/api/queryClient";

/** Every collection here is addressed by `_id`, apart from the one that overrides `toDetailId`. */
type Identifiable = { _id: string };

export function seedQuery(queryKey: QueryKey, data: unknown) {
    // Every session field is optional; writing undefined would leave an entry reporting success with no data.
    if (data === undefined) return;

    // A seed only fills a gap: anything already cached — a fetch, a poll, an earlier seed — is at least as
    // good as session data, so it wins. Overwriting would re-stale it and force a refetch that reads as a flicker.
    // Asked of the data rather than dataUpdatedAt, which seeds set to 0 and so cannot distinguish
    // "never written" from "seeded"; keying on data also still seeds a query whose first fetch is in flight.
    if (queryClient.getQueryData(queryKey) !== undefined) return;

    // Stale on arrival, so queries still make network requests on first mount, but can fall back to cache
    queryClient.setQueryData(queryKey, data, { updatedAt: 0 });
}

/**
 * Merges poll data into one query, claiming nothing about its freshness or validity.
 *
 * A merge that returns undefined writes nothing, for a caller that cannot act on what it found cached.
 */
function patchQuery<TData>(queryKey: QueryKey, merge: (cached: TData | undefined) => TData | undefined) {
    const state = queryClient.getQueryState<TData>(queryKey);

    const next = merge(state?.data);
    if (next === undefined) return;

    // Never claims freshness: an existing refetch deadline is kept as it was, and an untouched key starts
    // stale like a seed.
    queryClient.setQueryData(queryKey, next, { updatedAt: state?.dataUpdatedAt ?? 0 });

    // Writing marks the query settled, so put back the refetch it was owed: a merge can never remove
    // anything, so it cannot answer the deletion an invalidation is often about. Exact, or a list key
    // would drag its details in too.
    if (state?.isInvalidated) {
        queryClient.invalidateQueries({ queryKey, exact: true, refetchType: "none" });
    }
}

/** Options for seeding a collection. */
type SeedCollectionOptions<T> = {
    /** Whether the list endpoint wraps its array in an `{ items }` envelope. */
    enveloped?: boolean;
    /** A function to extract the detail id from an item, if its not the default `_id` field. */
    toDetailId?: (item: T) => string | undefined;
};

/**
 * Seeds a collection's list query (eg. getListDevices) and every entity's detail query for that resource (eg. getGetDevice-by-id)
 */
export function seedCollection<T extends Identifiable>(
    listQueryKey: QueryKey,
    toDetailQueryKey: (id: string) => QueryKey,
    items: T[] | undefined,
    { enveloped = false, toDetailId = (item) => item._id }: SeedCollectionOptions<T> = {},
) {
    seedQuery(listQueryKey, items && (enveloped ? { items } : items));

    items?.forEach((item) => {
        const detailId = toDetailId(item);
        // No id means the endpoint cannot address the entity either, so there is no key worth seeding.
        if (detailId) seedQuery(toDetailQueryKey(detailId), item);
    });
}

/**
 * Fans a poll's changed entities across one collection's list and detail queries.
 */
export function patchCollection<T extends Identifiable>(
    listQueryKey: QueryKey,
    toDetailQueryKey: (id: string) => QueryKey,
    updates: T[] | undefined,
) {
    // Returning early also avoids writing a fresh array reference into the list key, which would
    // re-render every subscriber on a tick that changed nothing.
    if (!updates?.length) return;

    // Detail keys always take the patch: a poll returns whole documents, so an entity the cache has never
    // seen can still be written as it stands.
    updates.forEach((update) => {
        patchQuery<T>(toDetailQueryKey(update._id), (cached) => (cached ? { ...cached, ...update } : update));
    });

    // The list key does not, unless something has already fetched it. A poll carries only the entities that
    // changed, so writing them cold would present a handful as the whole collection, and the key carries no
    // filter to say otherwise — the same reason the seed skips Images.
    patchQuery<T[]>(listQueryKey, (cached) => (cached ? mergeUpdatesIntoList(cached, updates) : undefined));
}

/** Combines polled updates with a cached list, keeping entities the poll did not mention. */
function mergeUpdatesIntoList<T extends Identifiable>(cached: T[], updates: T[]): T[] {
    const updateById = new Map(updates.map((update) => [update._id, update]));
    const cachedIds = new Set(cached.map((item) => item._id));

    return [
        // Existing order is preserved; re-sorting would reshuffle a rendered list on every poll tick.
        ...cached.map((item) => {
            const update = updateById.get(item._id);
            return update ? { ...item, ...update } : item;
        }),
        ...updates.filter((update) => !cachedIds.has(update._id)),
    ];
}
